import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import AccessibleButton from '../components/common/AccessibleButton';
import PageHeader from '../components/common/PageHeader';
import CareerDirectionCard from '../features/careerDirection/CareerDirectionCard';
import { stripInternalFields } from '../utils/privacy';

const TABS = [
    { id: 'basic', label: 'Personal' },
    { id: 'education', label: 'Education' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'career', label: 'Career' },
    { id: 'mentor_matching', label: 'Mentor Matching' },
    { id: 'links', label: 'Links' }
];

const emptyForm = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    city: 'Lahore',
    country: 'Pakistan',
    location: '',
    bio: '',
    university: '',
    degree: '',
    major: '',
    minor: '',
    graduation_year: '',
    year_of_study: 'Final year',
    gpa: '',
    work_status: 'not_yet',
    career_goals: '',
    interested_roles: ['AI Implementation Engineer', 'Business Analyst'],
    industry_pref: 'Construction',
    hours_per_week: 'Under 10 hours',
    visit_preference: 'Yes, in my city',
    ranking_interests: [
        'AI & LLM systems',
        'Implementation & delivery',
        'Business consulting & discovery',
        'AI agents & automation',
        'ERP & business systems',
        'Data & integration'
    ],
    improvement_skill: '',
    feedback_pref: 'Written notes I can re-read',
    checkin_freq: 'Once a week',
    work_style_1: 'alone',
    work_style_2: 'steps',
    work_style_3: 'ask_early',
    linkedin_url: '',
    github_url: '',
    portfolio_url: '',
    nda_accepted: false,
    skills: {
        'Prompting for business workflows': 'some',
        'AI & ML fundamentals': 'some',
        'RAG & enterprise knowledge': 'learning',
        'Process mapping': 'learning',
        'Stakeholder interviews': 'learning'
    }
};

const Profile = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');

    const [experienceForm, setExperienceForm] = useState({ company: '', title: '', description: '', start_date: '', end_date: '' });
    const [educationExtra, setEducationExtra] = useState({ institution: '', degree: '', field_of_study: '', start_date: '', end_date: '' });

    const populateForm = useCallback((data) => {
        const safe = stripInternalFields(data) || {};
        const userData = safe.User || {};
        setForm((prev) => ({
            ...prev,
            first_name: userData.first_name || user?.first_name || prev.first_name,
            last_name: userData.last_name || user?.last_name || prev.last_name,
            email: userData.email || user?.email || prev.email,
            phone: userData.phone || user?.phone || prev.phone,
            city: safe.city || safe.location || prev.city,
            country: safe.country || prev.country,
            location: safe.location || safe.city || prev.location,
            bio: safe.bio || prev.bio,
            university: safe.university || '',
            // "Degree or program" is the visible field; the column behind it is
            // `major`. Read it back from there so the input shows what was
            // actually stored rather than a placeholder.
            degree: safe.degree || safe.major || '',
            major: safe.major || safe.degree || '',
            minor: safe.minor || prev.minor,
            graduation_year: safe.graduation_year || '',
            gpa: safe.gpa || prev.gpa,
            career_goals: safe.career_goals || '',
            linkedin_url: safe.linkedin_url || prev.linkedin_url,
            github_url: safe.github_url || prev.github_url,
            portfolio_url: safe.portfolio_url || prev.portfolio_url,
            improvement_skill: safe.improvement_skill || prev.improvement_skill || '',
            year_of_study: safe.year_of_study || '',
            work_status: safe.work_status || '',
            hours_per_week: safe.hours_per_week || '',
            industry_pref: safe.industry_pref || '',
            checkin_freq: safe.checkin_freq || '',
            feedback_pref: safe.feedback_pref || '',
            nda_accepted: Boolean(safe.nda_accepted)
        }));
        setProfile(safe);
    }, [user]);

    const loadProfile = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get('/profile');
            populateForm(response.data.data);
        } catch (error) {
            if (error.response?.status === 404) {
                setProfile(null);
                setForm((prev) => ({
                    ...prev,
                    first_name: user?.first_name || '',
                    last_name: user?.last_name || '',
                    email: user?.email || '',
                    phone: user?.phone || ''
                }));
            } else {
                toast.error('Unable to load your talent profile right now.');
            }
        } finally {
            setLoading(false);
        }
    }, [populateForm, user]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    const onChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const toggleRoleInterest = (role) => {
        setForm((prev) => {
            const current = prev.interested_roles || [];
            if (current.includes(role)) {
                return { ...prev, interested_roles: current.filter(r => r !== role) };
            } else {
                return { ...prev, interested_roles: [...current, role] };
            }
        });
    };

    const setSkillRating = (skillName, rating) => {
        setForm((prev) => ({
            ...prev,
            skills: { ...prev.skills, [skillName]: rating }
        }));
    };

    const calculateCompletion = () => {
        const reqs = [
            { id: 'first_name', name: 'First name', value: form.first_name, tab: 'basic' },
            { id: 'last_name', name: 'Last name', value: form.last_name, tab: 'basic' },
            { id: 'email', name: 'Email', value: form.email || user?.email, tab: 'basic' },
            { id: 'phone', name: 'Phone', value: form.phone, tab: 'basic' },
            { id: 'city', name: 'City / Country', value: form.city || form.location, tab: 'basic' },
            { id: 'university', name: 'University', value: form.university, tab: 'education' },
            { id: 'degree', name: 'Degree or program', value: form.degree || form.major, tab: 'education' },
            { id: 'graduation_year', name: 'Graduation year', value: form.graduation_year, tab: 'education' },
            { id: 'improvement_skill', name: 'Development target', value: form.improvement_skill, tab: 'mentor_matching' }
        ];

        const done = reqs.filter(r => r.value && String(r.value).trim().length > 0);
        const missing = reqs.filter(r => !r.value || String(r.value).trim().length === 0);

        return {
            count: done.length,
            total: 9,
            missing,
            isComplete: done.length >= 9
        };
    };

    const saveProfile = async (returnAfter = false) => {
        setSaving(true);
        try {
            const payload = {
                first_name: form.first_name,
                last_name: form.last_name,
                phone: form.phone,
                location: form.city || form.location,
                city: form.city,
                country: form.country,
                university: form.university,
                degree: form.degree || form.major,
                // The field the intern can actually see and edit takes
                // precedence; `major` is the column it is stored in.
                major: form.degree || form.major,
                graduation_year: form.graduation_year ? Number(form.graduation_year) : null,
                gpa: form.gpa ? Number(form.gpa) : null,
                career_goals: form.career_goals,
                linkedin_url: form.linkedin_url || null,
                github_url: form.github_url || null,
                portfolio_url: form.portfolio_url || null,
                improvement_skill: form.improvement_skill || null,
                // Everything below was editable in the form but never sent, so
                // it reverted on the next load however many times it was saved.
                bio: form.bio,
                minor: form.minor,
                year_of_study: form.year_of_study || null,
                work_status: form.work_status || null,
                hours_per_week: form.hours_per_week || null,
                industry_pref: form.industry_pref || null,
                checkin_freq: form.checkin_freq || null,
                feedback_pref: form.feedback_pref || null,
                nda_accepted: Boolean(form.nda_accepted)
            };

            const response = await axios.put('/profile', payload);
            const updated = stripInternalFields(response.data.data);
            populateForm(updated);
            toast.success('Profile saved successfully');
            if (returnAfter) navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const addExperience = (e) => {
        e.preventDefault();
        if (!experienceForm.title || !experienceForm.company) return;
        const newExp = { id: Date.now(), ...experienceForm };
        setProfile((prev) => ({
            ...prev,
            workExperiences: [...(prev?.workExperiences || []), newExp]
        }));
        setExperienceForm({ company: '', title: '', description: '', start_date: '', end_date: '' });
        toast.success('Work experience added');
    };

    const deleteExperience = (id) => {
        setProfile((prev) => ({
            ...prev,
            workExperiences: (prev?.workExperiences || []).filter(e => e.id !== id)
        }));
        toast.success('Work experience removed');
    };

    const addEducationRow = (e) => {
        e.preventDefault();
        if (!educationExtra.institution || !educationExtra.degree) return;
        const newEdu = { id: Date.now(), ...educationExtra };
        setProfile((prev) => ({
            ...prev,
            educations: [...(prev?.educations || []), newEdu]
        }));
        setEducationExtra({ institution: '', degree: '', field_of_study: '', start_date: '', end_date: '' });
        toast.success('Education entry added');
    };

    const deleteEducation = (id) => {
        setProfile((prev) => ({
            ...prev,
            educations: (prev?.educations || []).filter(e => e.id !== id)
        }));
        toast.success('Education entry removed');
    };

    const comp = calculateCompletion();
    const percent = Math.round((comp.count / comp.total) * 100);

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-64" />
                <div className="skeleton h-24" />
                <div className="skeleton h-80" />
            </div>
        );
    }

    return (
        <div className="w-full space-y-6 overflow-x-hidden pb-16">
            <PageHeader
                title="Talent Profile"
                description="Finish the 9 required fields and Module 1 unlocks straight away."
            />

            <CareerDirectionCard />

            {/* Profile Completion Overview Banner */}
            <section className="bg-surface border border-brand-200/60 rounded-2xl p-5 md:p-6 shadow-sm space-y-4 animate-fade-up">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="font-serif text-2xl font-bold text-ink-950">
                                {comp.count} / {comp.total} required profile fields complete
                            </h2>
                            <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${comp.isComplete ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                {comp.isComplete ? 'Profile setup complete (100%)' : `Incomplete (${percent}%)`}
                            </span>
                        </div>
                        <p className="text-sm text-ink-600 mt-1">
                            {comp.isComplete
                                ? 'All 9 required fields are submitted. Your training and assignment modules are unlocked.'
                                : 'Fill in the missing fields below so your mentor can pair you with suitable client projects.'
                            }
                        </p>
                    </div>

                    <AccessibleButton
                        onClick={() => saveProfile(false)}
                        loading={saving}
                        className="bg-brand-700 hover:bg-brand-800 text-white font-medium text-sm px-5 py-2.5 rounded-xl transition-colors shadow-sm self-start sm:self-auto"
                    >
                        Save Profile
                    </AccessibleButton>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-ink-100 h-2.5 rounded-full overflow-hidden">
                    <div
                        className={`h-full transition-all duration-500 ${comp.isComplete ? 'bg-emerald-600' : 'bg-brand-600'}`}
                        style={{ width: `${percent}%` }}
                    />
                </div>

                {/* Missing required field pills */}
                {comp.missing.length > 0 && (
                    <div className="pt-2">
                        <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-2">
                            Missing fields to finish profile:
                        </p>
                        <ul className="flex flex-wrap gap-2" aria-label="Missing fields">
                            {comp.missing.map((item) => (
                                <li key={item.id}>
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900 text-xs font-medium rounded-full transition-colors"
                                        onClick={() => setActiveTab(item.tab)}
                                    >
                                        <span>+ {item.name}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>

            {/* 7-Tab Container */}
            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm animate-fade-up">
                <div className="border-b border-border bg-canvas/40 px-3 pt-2">
                    <nav className="flex overflow-x-auto gap-1" aria-label="Profile sections">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={activeTab === tab.id ? 'page' : undefined}
                                className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap min-h-[44px] transition-colors rounded-t-lg ${
                                    activeTab === tab.id
                                        ? 'border-brand-700 text-brand-900 bg-surface font-semibold shadow-xs'
                                        : 'border-transparent text-ink-600 hover:text-ink-950 hover:bg-surface/50'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-5 md:p-8 space-y-6">
                    {/* TAB 1: PERSONAL */}
                    {activeTab === 'basic' && (
                        <div className="space-y-6">
                            <h3 className="font-serif text-xl font-bold text-ink-950">Personal Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="label" htmlFor="first_name">
                                        First name <span className="text-red-500">*</span>
                                    </label>
                                    <input id="first_name" name="first_name" className="input-field" value={form.first_name} onChange={onChange} required />
                                </div>
                                <div>
                                    <label className="label" htmlFor="last_name">
                                        Last name <span className="text-red-500">*</span>
                                    </label>
                                    <input id="last_name" name="last_name" className="input-field" value={form.last_name} onChange={onChange} required />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="label" htmlFor="email">
                                        Email address <span className="text-red-500">*</span>
                                    </label>
                                    <input id="email" className="input-field bg-ink-50 text-ink-600" value={form.email || user?.email || ''} disabled />
                                </div>
                                <div>
                                    <label className="label" htmlFor="phone">
                                        Phone number <span className="text-red-500">*</span>
                                    </label>
                                    <input id="phone" name="phone" className="input-field" placeholder="+92 300 0000000" value={form.phone} onChange={onChange} required />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="label" htmlFor="city">
                                        City <span className="text-red-500">*</span>
                                    </label>
                                    <input id="city" name="city" className="input-field" placeholder="e.g. Lahore, Karachi, Islamabad" value={form.city} onChange={onChange} required />
                                </div>
                                <div>
                                    <label className="label" htmlFor="country">
                                        Country <span className="text-red-500">*</span>
                                    </label>
                                    <input id="country" name="country" className="input-field" value={form.country} onChange={onChange} required />
                                </div>
                            </div>

                            <div>
                                <label className="label" htmlFor="bio">Professional summary</label>
                                <textarea id="bio" name="bio" rows={4} className="input-field" value={form.bio} onChange={onChange} placeholder="Briefly introduce yourself, your domain interest, and your background." />
                            </div>
                        </div>
                    )}

                    {/* TAB 2: EDUCATION */}
                    {activeTab === 'education' && (
                        <div className="space-y-6">
                            <h3 className="font-serif text-xl font-bold text-ink-950">Current Education</h3>
                            <div className="space-y-5">
                                <div>
                                    <label className="label" htmlFor="university">
                                        Institution / University <span className="text-red-500">*</span>
                                    </label>
                                    <input id="university" name="university" className="input-field" value={form.university} onChange={onChange} placeholder="e.g. NUST / NED University / FAST-NUCES / LUMS" required />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="label" htmlFor="degree">
                                            Degree / Program <span className="text-red-500">*</span>
                                        </label>
                                        <input id="degree" name="degree" className="input-field" value={form.degree} onChange={onChange} placeholder="e.g. BS Computer Science" required />
                                    </div>
                                    <div>
                                        <label className="label" htmlFor="minor">Minor (optional)</label>
                                        <input id="minor" name="minor" className="input-field" value={form.minor} onChange={onChange} placeholder="e.g. Data Science" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                    <div>
                                        <label className="label" htmlFor="graduation_year">
                                            Graduation year <span className="text-red-500">*</span>
                                        </label>
                                        <input id="graduation_year" name="graduation_year" type="number" className="input-field" value={form.graduation_year} onChange={onChange} placeholder="2025" required />
                                    </div>
                                    <div>
                                        <label className="label" htmlFor="year_of_study">Year of study</label>
                                        <select id="year_of_study" name="year_of_study" className="input-field" value={form.year_of_study} onChange={onChange}>
                                            <option value="1st year">1st year</option>
                                            <option value="2nd year">2nd year</option>
                                            <option value="3rd year">3rd year</option>
                                            <option value="Final year">Final year</option>
                                            <option value="Graduated">Graduated</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="label" htmlFor="gpa">GPA (optional)</label>
                                        <input id="gpa" name="gpa" type="number" step="0.01" className="input-field" value={form.gpa} onChange={onChange} placeholder="3.8" />
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-border pt-6 space-y-4">
                                <h4 className="font-semibold text-ink-950 text-base">Additional Degrees or Certifications</h4>
                                {(profile?.educations || []).length === 0 ? (
                                    <p className="text-sm text-ink-500 italic">No extra education entries added.</p>
                                ) : (
                                    <ul className="space-y-3">
                                        {profile.educations.map((edu) => (
                                            <li key={edu.id} className="flex items-start justify-between gap-3 border border-border rounded-xl p-4 bg-canvas/30">
                                                <div>
                                                    <p className="font-medium text-ink-950">{edu.institution}</p>
                                                    <p className="text-sm text-ink-600">{edu.degree}{edu.field_of_study ? ` · ${edu.field_of_study}` : ''}</p>
                                                </div>
                                                <AccessibleButton variant="outline" size="sm" ariaLabel={`Remove ${edu.institution}`} onClick={() => deleteEducation(edu.id)}>
                                                    Remove
                                                </AccessibleButton>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <form onSubmit={addEducationRow} className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-border rounded-xl p-4 bg-surface">
                                    <input className="input-field" placeholder="Institution" value={educationExtra.institution} onChange={(e) => setEducationExtra({ ...educationExtra, institution: e.target.value })} required />
                                    <input className="input-field" placeholder="Degree / Certificate" value={educationExtra.degree} onChange={(e) => setEducationExtra({ ...educationExtra, degree: e.target.value })} required />
                                    <input className="input-field md:col-span-2" placeholder="Field of study (optional)" value={educationExtra.field_of_study} onChange={(e) => setEducationExtra({ ...educationExtra, field_of_study: e.target.value })} />
                                    <AccessibleButton type="submit" variant="outline" className="md:col-span-2">Add Education Entry</AccessibleButton>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* TAB 3: EXPERIENCE */}
                    {activeTab === 'experience' && (
                        <div className="space-y-6">
                            <h3 className="font-serif text-xl font-bold text-ink-950">Work & Internship Experience</h3>

                            <div>
                                <label className="label mb-2">Have you completed prior internships or full-time roles?</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-ink-800">
                                        <input type="radio" name="work_status" value="yes" checked={form.work_status === 'yes'} onChange={onChange} className="text-brand-600 focus:ring-brand-500" />
                                        Yes, I have experience
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-ink-800">
                                        <input type="radio" name="work_status" value="not_yet" checked={form.work_status === 'not_yet'} onChange={onChange} className="text-brand-600 focus:ring-brand-500" />
                                        Not yet (this is my first program)
                                    </label>
                                </div>
                            </div>

                            <div className="space-y-4 pt-2">
                                <h4 className="font-semibold text-ink-950 text-base">Experience Entries</h4>
                                {(profile?.workExperiences || []).length === 0 ? (
                                    <p className="text-sm text-ink-500 italic">No work or internship entries added yet.</p>
                                ) : (
                                    <ul className="space-y-3">
                                        {profile.workExperiences.map((exp) => (
                                            <li key={exp.id} className="flex items-start justify-between gap-3 border border-border rounded-xl p-4 bg-canvas/30">
                                                <div>
                                                    <p className="font-semibold text-ink-950">{exp.title}</p>
                                                    <p className="text-sm text-ink-700 font-medium">{exp.company}</p>
                                                    {exp.description && <p className="text-sm text-ink-600 mt-1">{exp.description}</p>}
                                                </div>
                                                <AccessibleButton variant="outline" size="sm" ariaLabel={`Remove ${exp.title}`} onClick={() => deleteExperience(exp.id)}>
                                                    Remove
                                                </AccessibleButton>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <form onSubmit={addExperience} className="space-y-4 border border-border rounded-xl p-4 bg-surface">
                                    <h5 className="text-sm font-semibold text-ink-950">Add Experience</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input className="input-field" placeholder="Company / Organization" value={experienceForm.company} onChange={(e) => setExperienceForm({ ...experienceForm, company: e.target.value })} required />
                                        <input className="input-field" placeholder="Role / Title" value={experienceForm.title} onChange={(e) => setExperienceForm({ ...experienceForm, title: e.target.value })} required />
                                    </div>
                                    <textarea className="input-field" rows={3} placeholder="Key responsibilities and achievements..." value={experienceForm.description} onChange={(e) => setExperienceForm({ ...experienceForm, description: e.target.value })} />
                                    <AccessibleButton type="submit" variant="outline">Add Experience Entry</AccessibleButton>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* TAB 4: SKILLS */}
                    {activeTab === 'skills' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="font-serif text-xl font-bold text-ink-950">Core Internship Skill Assessment</h3>
                                <p className="text-sm text-ink-600 mt-1">Rate your current comfort level with key competencies so we can tailor training.</p>
                            </div>

                            <div className="space-y-4">
                                {Object.entries(form.skills).map(([skillName, rating]) => (
                                    <div key={skillName} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border rounded-xl bg-canvas/30">
                                        <span className="font-medium text-ink-950 text-sm">{skillName}</span>
                                        <div className="flex gap-2">
                                            {['learning', 'some', 'strong'].map((level) => (
                                                <button
                                                    key={level}
                                                    type="button"
                                                    onClick={() => setSkillRating(skillName, level)}
                                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg capitalize transition-all ${
                                                        rating === level
                                                            ? 'bg-brand-700 text-white shadow-xs'
                                                            : 'bg-surface border border-border text-ink-600 hover:text-ink-950'
                                                    }`}
                                                >
                                                    {level}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* TAB 5: CAREER */}
                    {activeTab === 'career' && (
                        <div className="space-y-6">
                            <h3 className="font-serif text-xl font-bold text-ink-950">Career Orientation</h3>

                            <div>
                                <label className="label" htmlFor="career_goals">Career Goals & Vision</label>
                                <textarea id="career_goals" name="career_goals" rows={3} className="input-field" value={form.career_goals} onChange={onChange} placeholder="What impact do you want to create in your target industry?" />
                            </div>

                            <div>
                                <label className="label mb-2">Roles you are most interested in pursuing:</label>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        'AI Implementation Engineer',
                                        'Business Analyst',
                                        'Data Engineer',
                                        'Solutions Architect',
                                        'Product Specialist',
                                        'Consultant'
                                    ].map((role) => {
                                        const isSelected = (form.interested_roles || []).includes(role);
                                        return (
                                            <button
                                                key={role}
                                                type="button"
                                                onClick={() => toggleRoleInterest(role)}
                                                className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                                                    isSelected
                                                        ? 'bg-brand-700 text-white shadow-xs'
                                                        : 'bg-canvas border border-border text-ink-700 hover:bg-surface'
                                                }`}
                                            >
                                                {isSelected ? '✓ ' : '+ '}{role}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="label" htmlFor="industry_pref">Target Industry Sector</label>
                                    <select id="industry_pref" name="industry_pref" className="input-field" value={form.industry_pref} onChange={onChange}>
                                        <option value="Construction">Construction & Engineering</option>
                                        <option value="Manufacturing">Manufacturing & Supply Chain</option>
                                        <option value="Healthcare">Healthcare & Biotech</option>
                                        <option value="Finance">Financial Services & Fintech</option>
                                        <option value="Retail">Retail & E-commerce</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label" htmlFor="hours_per_week">Weekly Availability</label>
                                    <select id="hours_per_week" name="hours_per_week" className="input-field" value={form.hours_per_week} onChange={onChange}>
                                        <option value="Under 10 hours">Under 10 hours / week</option>
                                        <option value="10-20 hours">10 - 20 hours / week</option>
                                        <option value="20-30 hours">20 - 30 hours / week</option>
                                        <option value="Full-time">Full-time (40 hours)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB 6: MENTOR MATCHING */}
                    {activeTab === 'mentor_matching' && (
                        <div className="space-y-6">
                            <h3 className="font-serif text-xl font-bold text-ink-950">Mentor Matching & Preferences</h3>

                            <div>
                                <label className="label" htmlFor="improvement_skill">
                                    What area / skill do you most want to improve during this program? <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="improvement_skill"
                                    name="improvement_skill"
                                    className="input-field"
                                    value={form.improvement_skill}
                                    onChange={onChange}
                                    placeholder="e.g. Enterprise AI prompt engineering & client presentation"
                                    required
                                />
                                <p className="text-xs text-ink-500 mt-1">This field is required for your mentor pairing.</p>
                            </div>

                            <div>
                                <label className="label mb-2">Preferred Feedback Style</label>
                                <div className="space-y-2">
                                    {[
                                        'Written notes I can re-read',
                                        '1-on-1 verbal feedback calls',
                                        'Direct code / document review comments'
                                    ].map((opt) => (
                                        <label key={opt} className="flex items-center gap-3 p-3 border border-border rounded-xl cursor-pointer hover:bg-canvas/40 transition-colors">
                                            <input
                                                type="radio"
                                                name="feedback_pref"
                                                value={opt}
                                                checked={form.feedback_pref === opt}
                                                onChange={onChange}
                                                className="text-brand-600 focus:ring-brand-500"
                                            />
                                            <span className="text-sm text-ink-900 font-medium">{opt}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="label mb-2">Check-in Frequency Preference</label>
                                <div className="flex gap-4">
                                    {['Twice a week', 'Once a week', 'Bi-weekly'].map((freq) => (
                                        <label key={freq} className="flex items-center gap-2 cursor-pointer text-sm text-ink-800 font-medium">
                                            <input
                                                type="radio"
                                                name="checkin_freq"
                                                value={freq}
                                                checked={form.checkin_freq === freq}
                                                onChange={onChange}
                                                className="text-brand-600 focus:ring-brand-500"
                                            />
                                            {freq}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB 7: LINKS */}
                    {activeTab === 'links' && (
                        <div className="space-y-6">
                            <h3 className="font-serif text-xl font-bold text-ink-950">Links & Verification</h3>

                            <div>
                                <label className="label" htmlFor="linkedin_url">LinkedIn URL</label>
                                <input
                                    id="linkedin_url"
                                    name="linkedin_url"
                                    type="url"
                                    className="input-field"
                                    value={form.linkedin_url}
                                    onChange={onChange}
                                    placeholder="https://www.linkedin.com/in/your-profile"
                                />
                            </div>

                            <div>
                                <label className="label" htmlFor="github_url">GitHub URL (optional)</label>
                                <input
                                    id="github_url"
                                    name="github_url"
                                    type="url"
                                    className="input-field"
                                    value={form.github_url}
                                    onChange={onChange}
                                    placeholder="https://github.com/your-username"
                                />
                            </div>

                            <div>
                                <label className="label" htmlFor="portfolio_url">Portfolio or Personal Website (optional)</label>
                                <input
                                    id="portfolio_url"
                                    name="portfolio_url"
                                    type="url"
                                    className="input-field"
                                    value={form.portfolio_url}
                                    onChange={onChange}
                                    placeholder="https://yourwebsite.com"
                                />
                            </div>

                            <div className="pt-4 border-t border-border">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="nda_accepted"
                                        checked={form.nda_accepted}
                                        onChange={onChange}
                                        className="mt-1 rounded text-brand-600 focus:ring-brand-500"
                                    />
                                    <span className="text-sm text-ink-700">
                                        I agree to the program confidentiality guidelines and non-disclosure terms regarding client data.
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Footer Actions Bar */}
                    <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <AccessibleButton
                                onClick={() => saveProfile(false)}
                                loading={saving}
                                className="bg-brand-700 hover:bg-brand-800 text-white text-sm font-medium px-5 py-2.5 rounded-xl shadow-xs transition-colors w-full sm:w-auto"
                            >
                                Save Profile
                            </AccessibleButton>
                            <AccessibleButton
                                variant="outline"
                                onClick={() => saveProfile(true)}
                                loading={saving}
                                className="w-full sm:w-auto"
                            >
                                Save & Return Home
                            </AccessibleButton>
                        </div>
                        <Link to="/journey" className="text-sm font-medium text-brand-700 hover:text-brand-900 transition-colors">
                            View Program Roadmap →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
